import { useCallback, useEffect, useRef, useState } from "react";

export const GEOLOCATION_STATES = {
  IDLE: "idle",
  LOCATING: "locating",
  FOUND: "found",
  DENIED: "denied",
  UNAVAILABLE: "unavailable",
};

const SESSION_LOCATION_KEY = "midpoint_cv_user_location";

function readSessionLocation() {
  try {
    const raw = sessionStorage.getItem(SESSION_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.latitude) || !Number.isFinite(parsed?.longitude)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionLocation(coordinates) {
  try {
    sessionStorage.setItem(
      SESSION_LOCATION_KEY,
      JSON.stringify({
        ...coordinates,
        capturedAt: new Date().toISOString(),
      })
    );
  } catch {
    // ignore
  }
}

function clearSessionLocation() {
  try {
    sessionStorage.removeItem(SESSION_LOCATION_KEY);
  } catch {
    // ignore
  }
}

export function useClientViewGeolocation({ disabled = false } = {}) {
  const [locationState, setLocationState] = useState(GEOLOCATION_STATES.IDLE);
  const [locationError, setLocationError] = useState("");
  const [coordinates, setCoordinates] = useState(null);
  const [requestId, setRequestId] = useState(0);
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    if (disabled) return;
    const cached = readSessionLocation();
    if (cached) {
      setCoordinates(cached);
      setLocationState(GEOLOCATION_STATES.FOUND);
    }
  }, [disabled]);

  const clearLocation = useCallback(() => {
    hasRequestedRef.current = false;
    setCoordinates(null);
    setLocationState(GEOLOCATION_STATES.IDLE);
    setLocationError("");
    clearSessionLocation();
  }, []);

  const requestCurrentLocation = useCallback((options = {}) => {
    if (disabled) return;
    if (options.force) {
      hasRequestedRef.current = false;
    }
    if (!options.force && (hasRequestedRef.current || locationState === GEOLOCATION_STATES.FOUND)) {
      return;
    }
    if (!options.force && locationState === GEOLOCATION_STATES.LOCATING) {
      return;
    }
    if (!options.force && locationState === GEOLOCATION_STATES.DENIED) {
      return;
    }

    hasRequestedRef.current = true;
    setRequestId((current) => current + 1);

    if (!navigator.geolocation) {
      setLocationState(GEOLOCATION_STATES.UNAVAILABLE);
      setLocationError("Geolocation is not supported on this device.");
      return;
    }

    setLocationState(GEOLOCATION_STATES.LOCATING);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          method: "gps",
        };
        setCoordinates(next);
        setLocationState(GEOLOCATION_STATES.FOUND);
        setLocationError("");
        writeSessionLocation(next);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationState(GEOLOCATION_STATES.DENIED);
          setLocationError("Location permission denied.");
          return;
        }
        hasRequestedRef.current = false;
        setLocationState(GEOLOCATION_STATES.UNAVAILABLE);
        setLocationError("Unable to determine your location.");
      },
      {
        enableHighAccuracy: false,
        maximumAge: 0,
        timeout: 12000,
      }
    );
  }, [disabled, locationState]);

  return {
    locationState,
    locationError,
    coordinates,
    requestId,
    requestCurrentLocation,
    clearLocation,
    hasLocation: locationState === GEOLOCATION_STATES.FOUND && Boolean(coordinates),
    isLocating: locationState === GEOLOCATION_STATES.LOCATING,
  };
}

/**
 * Request visitor location when a map is shown (once per session unless already granted/denied).
 */
export function useAutoRequestClientViewGeolocation(geolocation) {
  useEffect(() => {
    if (!geolocation?.requestLocation || geolocation.disabled) {
      return;
    }
    if (!geolocation.sharingReady || geolocation.sharingEnabled === false) {
      return;
    }
    if (geolocation.locationState === GEOLOCATION_STATES.DENIED) {
      return;
    }
    if (geolocation.hasLocation || geolocation.isLocating) {
      return;
    }
    geolocation.requestLocation();
  }, [geolocation]);
}

const USER_LOCATION_SOURCE_ID = "cv-user-location";
const USER_LOCATION_HALO_LAYER_ID = "cv-user-location-halo";
const USER_LOCATION_DOT_LAYER_ID = "cv-user-location-dot";

const USER_DOT_COLOR = "#2563eb";
const USER_HALO_COLOR = "#3b82f6";

function coordinatesToUserLocationGeoJson(coordinates) {
  const latitude = Number(coordinates?.latitude);
  const longitude = Number(coordinates?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { type: "FeatureCollection", features: [] };
  }

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [longitude, latitude],
        },
        properties: {},
      },
    ],
  };
}

function ensureUserLocationLayers(map) {
  if (!map.getSource(USER_LOCATION_SOURCE_ID)) {
    map.addSource(USER_LOCATION_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  if (!map.getLayer(USER_LOCATION_HALO_LAYER_ID)) {
    map.addLayer({
      id: USER_LOCATION_HALO_LAYER_ID,
      type: "circle",
      source: USER_LOCATION_SOURCE_ID,
      paint: {
        "circle-radius": 18,
        "circle-color": USER_HALO_COLOR,
        "circle-opacity": 0.25,
        "circle-stroke-width": 0,
      },
    });
  }

  if (!map.getLayer(USER_LOCATION_DOT_LAYER_ID)) {
    map.addLayer({
      id: USER_LOCATION_DOT_LAYER_ID,
      type: "circle",
      source: USER_LOCATION_SOURCE_ID,
      paint: {
        "circle-radius": 7,
        "circle-color": USER_DOT_COLOR,
        "circle-opacity": 1,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    });
  }

  try {
    map.moveLayer(USER_LOCATION_HALO_LAYER_ID);
    map.moveLayer(USER_LOCATION_DOT_LAYER_ID);
  } catch {
    // Layer order is best-effort when the style is still settling.
  }
}

function updateUserLocationData(map, coordinates) {
  const source = map.getSource(USER_LOCATION_SOURCE_ID);
  if (!source) return;
  source.setData(coordinatesToUserLocationGeoJson(coordinates));
}

/**
 * Renders a blue dot for the visitor's location on a Mapbox map.
 */
export function useMapUserLocationLayer(mapRef, coordinates, mapReady = true) {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) {
      return undefined;
    }

    const apply = () => {
      if (!map.isStyleLoaded()) {
        return;
      }
      ensureUserLocationLayers(map);
      updateUserLocationData(map, coordinates);
    };

    apply();
    map.on("load", apply);

    return () => {
      map.off("load", apply);
    };
  }, [coordinates, mapReady, mapRef]);
}
